import { useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import type Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import type { IChangeEvent } from '@rjsf/core';
import type { RJSFSchema, ErrorSchema } from '@rjsf/utils';
import type { IFormSchema, IFormStep } from '~/types';
import { ShadcnForm } from './ShadcnForm';
import { PillRadioWidget } from './PillRadioWidget';
import { IframeWidget } from './IframeWidget';

// Register custom widgets
const customWidgets = {
  iframe: IframeWidget,
  radio: PillRadioWidget,
};

/**
 * Filters a ui:layoutGrid to only include children that belong to the given step fields.
 * Deep-clones the grid structure and removes columns referencing fields not in this step.
 * Returns undefined if no children remain after filtering.
 */
function filterLayoutGridForStep(
  layoutGrid: Record<string, unknown>,
  stepFields: string[],
): Record<string, unknown> | undefined {
  const row = layoutGrid['ui:row'] as Record<string, unknown> | undefined;
  if (!row) return undefined;

  const children = row.children as Array<Record<string, unknown>> | undefined;
  if (!children) return undefined;

  const filteredChildren = children.filter((col) => {
    const colDef = col['ui:col'] as Record<string, unknown> | undefined;
    if (!colDef) return false;

    const colChildren = colDef.children as string[] | undefined;
    if (!colChildren) return false;

    // Keep this column if any of its field names are in the step
    return colChildren.some((field) => stepFields.includes(field));
  });

  if (filteredChildren.length === 0) return undefined;

  return {
    'ui:row': {
      ...row,
      children: filteredChildren,
    },
  };
}

interface StepRendererProps {
  schema: IFormSchema;
  stepConfig: IFormStep;
  formData: Record<string, unknown>;
  stepErrors: Record<string, string>;
  onChange: (data: Record<string, unknown>) => void;
  onBlur?: () => void;
}

export interface StepRendererRef {
  validate: () => boolean;
}

export const StepRenderer = forwardRef<StepRendererRef, StepRendererProps>(
  ({ schema, stepConfig, formData, stepErrors, onChange, onBlur }, ref) => {
    const formRef = useRef<Form>(null);

    // Build subset schema for current step's fields only
    // Note: We don't include title/description here since ProgressBar shows the step title
    const stepSchema = useMemo(() => {
      const stepProperties: Record<string, unknown> = {};
      const stepRequired: string[] = [];

      for (const fieldName of stepConfig.fields) {
        if (schema.properties[fieldName]) {
          stepProperties[fieldName] = schema.properties[fieldName];
        }
        if (schema.required?.includes(fieldName)) {
          stepRequired.push(fieldName);
        }
      }

      return {
        type: 'object' as const,
        properties: stepProperties,
        required: stepRequired.length > 0 ? stepRequired : undefined,
      };
    }, [schema, stepConfig]);

    // Build subset uiSchema for current step's fields only
    // Includes per-field configs and any root-level ui:* properties (e.g. ui:layoutGrid)
    const stepUiSchema = useMemo(() => {
      if (!schema.uiSchema) return {};

      const uiSchema: Record<string, unknown> = {};

      // Copy root-level ui:* properties (layout grid, global options, etc.)
      for (const [key, value] of Object.entries(schema.uiSchema)) {
        if (key.startsWith('ui:')) {
          uiSchema[key] = value;
        }
      }

      // Copy per-field uiSchema entries for this step's fields
      for (const fieldName of stepConfig.fields) {
        if (schema.uiSchema[fieldName]) {
          uiSchema[fieldName] = schema.uiSchema[fieldName];
        }
      }

      // Filter ui:order to only include current step's fields
      if (uiSchema['ui:order']) {
        const order = uiSchema['ui:order'] as string[];
        uiSchema['ui:order'] = order.filter((f) => stepConfig.fields.includes(f));
      }

      // Filter ui:layoutGrid to only include current step's fields
      if (uiSchema['ui:layoutGrid']) {
        const filtered = filterLayoutGridForStep(
          uiSchema['ui:layoutGrid'] as Record<string, unknown>,
          stepConfig.fields,
        );
        if (filtered) {
          uiSchema['ui:layoutGrid'] = filtered;
        } else {
          // No layout grid children for this step — remove the grid directives
          delete uiSchema['ui:layoutGrid'];
          delete uiSchema['ui:field'];
        }
      }

      return uiSchema;
    }, [schema.uiSchema, stepConfig]);

    // Expose validate method to parent
    useImperativeHandle(ref, () => ({
      validate: () => {
        if (!formRef.current) return true;
        return formRef.current.validateForm();
      },
    }));

    const handleChange = (e: IChangeEvent) => {
      onChange(e.formData || {});
    };

    const handleBlur = () => {
      onBlur?.();
    };

    // Transform step errors to rjsf extraErrors format
    const extraErrors = useMemo(() => {
      const errors: Record<string, { __errors: string[] }> = {};
      for (const [field, message] of Object.entries(stepErrors)) {
        errors[field] = { __errors: [message] };
      }
      return errors;
    }, [stepErrors]);

    // Form context to pass data to widgets (like IframeWidget for Calendly prefill)
    const formContext = useMemo(
      () => ({
        formData,
      }),
      [formData],
    );

    return (
      <div className="step-form" onBlur={handleBlur}>
        <ShadcnForm
          ref={formRef}
          schema={stepSchema as RJSFSchema}
          uiSchema={stepUiSchema}
          validator={validator}
          widgets={customWidgets}
          onChange={handleChange}
          formData={formData}
          formContext={formContext}
          showErrorList={false}
          noValidate
          extraErrors={extraErrors as ErrorSchema}
        >
          {/* Hide default submit button - we use NavigationButtons instead */}
          <></>
        </ShadcnForm>
      </div>
    );
  },
);

StepRenderer.displayName = 'StepRenderer';
